import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import induceIcon from '../assets/induce_icon.svg';
import rhapsodyIcon from '../assets/rhapsody_icon.svg';
import { SignUpButton, TalkToFoundersButton } from './CTAButtons';
import { MobileNavMenu } from './nav/MobileNavMenu';
import { ProductNavDropdown } from './nav/ProductNavDropdown';
import { SolutionsNavDropdown } from './nav/SolutionsNavDropdown';
import { UseCasesNavDropdown } from './nav/UseCasesNavDropdown';

export function Nav() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const syncNavOffset = () => {
      document.documentElement.style.setProperty(
        '--shell-nav-offset',
        `${nav.getBoundingClientRect().height}px`,
      );
    };

    syncNavOffset();
    const observer = new ResizeObserver(syncNavOffset);
    observer.observe(nav);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const pct = max > 0 ? (window.scrollY / max) * 100 : 0;
      progressRef.current?.style.setProperty('--scroll', `${pct}%`);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      ref={navRef}
      className={`nav-bar fixed top-0 left-0 right-0 z-[110] [backdrop-filter:blur(28px)_saturate(140%)] [-webkit-backdrop-filter:blur(28px)_saturate(140%)] transition-[background,padding] duration-[280ms] ${scrolled
        ? 'py-[10px] bg-[rgba(14,14,14,0.82)]'
        : 'py-[14px] bg-[rgba(14,14,14,0.55)]'
        }`}
    >
      <div className="container relative flex w-full items-center">
        <Link
          to="/"
          aria-label="Induce home"
          className="flex items-center shrink-0"
          onClick={(e) => {
            if (pathname === '/') {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'auto' });
            }
          }}
        >
          <img
            src={induceIcon}
            alt=""
            width={32}
            height={32}
            className="w-36 sm:w-42 shrink-0 object-contain"
            aria-hidden
          />
        </Link>

        <div className="nav-links absolute left-1/2 hidden -translate-x-1/2 items-center gap-7 lg:flex">
          <ProductNavDropdown pathname={pathname} />
          <SolutionsNavDropdown pathname={pathname} />
          <Link
            to="/rhapsody"
            className="nav-rhapsody"
            aria-label="Rhapsody Engine"
            aria-current={pathname.startsWith('/rhapsody') ? 'page' : undefined}
          >
            <img src={rhapsodyIcon} alt="Rhapsody Engine" width={4756} height={549} />
          </Link>
          <UseCasesNavDropdown pathname={pathname} />
          <Link to="/pricing">Pricing</Link>
          <Link to="/blog">Blog</Link>
        </div>

        <div className="ml-auto flex items-center gap-3">
          <MobileNavMenu pathname={pathname} />
          <SignUpButton
            variant="ghost"
            analyticsLocation="nav"
            className="hidden sm:inline-flex !px-3 !py-2 !text-[10px] sm:!px-5 sm:!py-[13px] sm:!text-[12px]"
          />
          <TalkToFoundersButton variant="compact" analyticsLocation="nav" className="hidden sm:inline-flex" />
        </div>
      </div>

      <div ref={progressRef} className="nav-progress" />
    </nav>
  );
}
